/**
 * TopicRecommendedSwitch — the "Topic-based Recommended" on/off switch.
 *
 * State lives server-side in the `topic_recommendations` feature flag
 * (GET/PUT /admin/feature-flags). Flipping it asks for confirmation first,
 * in plain English, because it changes what every app user sees in Recommended.
 */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { fetchFeatureFlags, setFeatureFlag } from "../../../../actions/goalContentActions";
import { ToggleSwitch } from "../../../redesign/edit";

export const TOPIC_RECOMMENDATIONS_FLAG = "topic_recommendations";

const CONFIRM_ON =
  "Turn ON topic-based Recommended? People who picked topics will see their topic lists in Recommended. " +
  "People who already have category interests are not changed.";
const CONFIRM_OFF =
  "Turn OFF topic-based Recommended? Everyone without category interests will see the generic Recommended list.";

export default function TopicRecommendedSwitch() {
  const dispatch = useDispatch();
  const flags = useSelector(s => s.goalContent?.featureFlags);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    dispatch(fetchFeatureFlags());
  }, [dispatch]);

  const flag = Array.isArray(flags) ? flags.find(f => f.key === TOPIC_RECOMMENDATIONS_FLAG) : null;

  const handleChange = async () => {
    if (!flag || saving) return;
    const next = !flag.enabled;
    const result = await Swal.fire({
      icon: "question",
      title: next ? "Turn on topic-based Recommended?" : "Turn off topic-based Recommended?",
      text: next ? CONFIRM_ON : CONFIRM_OFF,
      showCancelButton: true,
      confirmButtonText: next ? "Turn on" : "Turn off",
    });
    if (!result?.isConfirmed) return;
    setSaving(true);
    try {
      await dispatch(setFeatureFlag(TOPIC_RECOMMENDATIONS_FLAG, next));
    } finally {
      setSaving(false);
    }
  };

  const updatedLabel = flag?.updated_at
    ? `Last changed ${new Date(flag.updated_at).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })}`
    : null;

  return (
    <div style={{ padding: "16px 28px 0" }}>
      <div
        className="bg-surface border border-sand flex items-center"
        style={{ borderRadius: 12, padding: "14px 16px", gap: 16, flexWrap: "wrap" }}
      >
        <div className="flex-1" style={{ minWidth: 260 }}>
          <div className="text-navy" style={{ fontSize: 14, fontWeight: 700 }}>
            Topic-based Recommended
          </div>
          <div className="text-text-muted" style={{ fontSize: 12, marginTop: 2 }}>
            ON: people with topics see their topic lists in Recommended (people with category interests are not
            changed). OFF: everyone without categories sees the generic Recommended list.
          </div>
          {updatedLabel && (
            <div className="text-text-muted" style={{ fontSize: 11.5, marginTop: 4 }}>
              {updatedLabel}
            </div>
          )}
        </div>
        {flag ? (
          <ToggleSwitch
            name="topic_recommendations"
            checked={flag.enabled}
            onChange={handleChange}
            disabled={saving}
            label={flag.enabled ? "On" : "Off"}
          />
        ) : (
          <span className="text-text-muted" style={{ fontSize: 12 }}>
            {Array.isArray(flags) ? "Switch not available" : "Loading…"}
          </span>
        )}
      </div>
    </div>
  );
}
